/* LandingAD — Wondernastic. Additive Impulse 7.6.2 integration.
 * No pixel initialization, artificial conversion events or automatic POST retries.
 * Server prices, availability, discounts and cart are authoritative.
 */
(() => {
  'use strict';
  if (window.__wnPerformanceLanding) return;
  window.__wnPerformanceLanding = true;
  if (!window.fetch || !window.FormData || !window.DOMParser || typeof AbortController !== 'function') return;

  const controllers = new Set();
  const boundRoots = new WeakMap();
  const boundDialogs = new WeakSet();
  const operation = { busy: false, uncertain: null, controller: null, activeCard: null, staleView: false, revision: 0 };
  let cartOpener = null;
  let previousOverflow = '';
  let scrollLocked = false;
  let fixedBodySnapshot = null;
  let countRevision = 0;
  let countRefreshScheduled = false;
  let nativeObservedElement = null;
  let nativeObserver = null;
  let lastCart = null;
  let nativeOpenPending = false;
  let cartOpenAttempt = null;
  let monsterObserver = null;
  let monsterObservedElement = null;
  let monsterOpener = null;

  const all = (scope, selector) => Array.from(scope.querySelectorAll(selector));
  const message = (element, text) => { if (element) element.textContent = text; };
  const getController = () => operation.controller && operation.controller.root.isConnected
    ? operation.controller : Array.from(controllers).find(item => item.root.isConnected);
  const variantId = value => String(value == null ? '' : value);
  const cartCount = cart => cart && typeof cart === 'object' && cart.item_count != null
    && Number.isFinite(Number(cart.item_count)) ? Number(cart.item_count) : null;
  const validCart = cart => cart && Array.isArray(cart.items) && Number.isInteger(cartCount(cart))
    && cartCount(cart) >= 0 && cart.items.every(item => item && (item.variant_id != null || item.id != null)
      && Number.isInteger(Number(item.quantity)) && Number(item.quantity) >= 0)
    && cart.items.reduce((sum, item) => sum + Number(item.quantity), 0) === cartCount(cart);

  function endpoint(value, kind) {
    const url = new URL(value, window.location.origin);
    const suffix = kind === 'cart' ? /\/cart(?:\.js)?\/?$/ : new RegExp('/cart/' + kind + '(?:\\.js)?/?$');
    if (url.origin !== window.location.origin || !suffix.test(url.pathname)) throw new Error('Ruta de carrito no válida.');
    url.pathname = url.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    url.search = '';
    url.hash = '';
    return url.pathname;
  }

  async function request(url, options, isMutation = false) {
    const abort = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = setTimeout(() => { if (abort) abort.abort(); }, isMutation ? 20000 : 12000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        ...(abort ? { signal: abort.signal } : {})
      });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch (_) {
        const error = new Error('No se ha podido confirmar la respuesta del carrito.');
        error.uncertain = isMutation;
        throw error;
      }
      if (!response.ok || Number(data.status) >= 400) {
        const description = typeof data.description === 'string' ? data.description
          : typeof data.message === 'string' ? data.message : 'No se ha podido actualizar el carrito.';
        const error = new Error(description);
        error.status = Number(data.status) || response.status;
        error.uncertain = isMutation && (response.status >= 500 || Number(data.status) >= 500);
        throw error;
      }
      return data;
    } catch (error) {
      if (isMutation && typeof error.uncertain !== 'boolean') error.uncertain = true;
      throw error;
    } finally { clearTimeout(timeout); }
  }

  function cartSection() { return document.querySelector('[data-wn-cart-section]'); }
  function sectionId() {
    const section = cartSection();
    if (!section) return '';
    const wrapper = section.closest('[id^="shopify-section-"]');
    return section.dataset.sectionId || section.dataset.wnCartSection || (wrapper && wrapper.id.replace('shopify-section-', '')) || '';
  }
  function dialog() { const section = cartSection(); return section && section.querySelector('[data-wn-cart-dialog]'); }
  function cartMessage(text) { message(cartSection() && cartSection().querySelector('[data-wn-cart-message]'), text); }

  function updateCount(count) {
    if (!Number.isInteger(count) || count < 0) return;
    countRevision++;
    all(document, '[data-wn-count], .cart-link__bubble-num').forEach(el => { el.textContent = String(count); });
    all(document, '.cart-link__bubble').forEach(el => el.classList.toggle('cart-link__bubble--visible', count > 0));
    all(document, '[data-wn-landing] .wn-cart-access').forEach(el => { el.hidden = count === 0; });
  }

  function imageSource(value) {
    if (typeof value !== 'string' || !value) return '';
    try { const url = new URL(value, location.origin); return url.protocol === 'https:' || url.origin === location.origin ? url.href : ''; }
    catch (_) { return ''; }
  }

  function thumbnailSource(value) {
    const source = imageSource(value);
    if (!source) return '';
    const url = new URL(source);
    if (url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'))) {
      url.searchParams.set('width', '160');
      return url.href;
    }
    return source;
  }

  function renderBundle(controller, cart) {
    const tray = controller.root.querySelector('[data-wn-bundle]');
    if (!tray) return;
    const eligible = new Map();
    controller.cards.filter(card => card.element.dataset.wnOfferEligible === 'true').forEach(card => {
      card.variants.forEach(variant => eligible.set(variantId(variant.id), { card, variant }));
    });
    const units = [];
    let total = 0;
    cart.items.forEach(item => {
      const match = eligible.get(variantId(item.variant_id || item.id));
      if (!match) return;
      const quantity = Number(item.quantity);
      total += quantity;
      for (let index = 0; index < quantity && units.length < 3; index++) units.push({ item, ...match });
    });
    all(tray, '[data-wn-slot]').forEach((slot, index) => {
      const unit = units[index];
      const empty = slot.querySelector('.wn-bundle-slot__empty');
      const img = slot.querySelector('[data-wn-slot-image]');
      const size = slot.querySelector('[data-wn-slot-size]');
      slot.classList.toggle('is-filled', Boolean(unit));
      slot.dataset.filled = String(Boolean(unit));
      if (empty) empty.hidden = Boolean(unit);
      if (!unit) {
        if (img) { img.hidden = true; img.removeAttribute('src'); img.alt = ''; }
        if (size) { size.hidden = true; size.textContent = ''; }
        slot.setAttribute('aria-label', 'Camiseta ' + (index + 1) + ': por elegir');
        return;
      }
      const title = String(unit.item.product_title || unit.item.title || unit.card.element.querySelector('h3')?.textContent || 'Camiseta');
      const sizeOption = Array.isArray(unit.item.options_with_values)
        ? unit.item.options_with_values.find(option => option && /^(talla|size|tamaño)$/i.test(String(option.name))) : null;
      const sizeIndex = Number(unit.card.element.dataset.wnSizeIndex);
      const variantText = unit.item.variant_title && unit.item.variant_title !== 'Default Title' ? unit.item.variant_title : '';
      const optionText = sizeOption ? String(sizeOption.value) : Number.isInteger(sizeIndex) && sizeIndex >= 0
        ? String(unit.variant.options[sizeIndex] || '') : String(variantText);
      const fallbackImage = unit.card.element.querySelector('[data-wn-product-image], [data-wn-image]');
      const src = thumbnailSource(unit.item.featured_image?.url || unit.item.image)
        || thumbnailSource(unit.variant.image) || thumbnailSource(fallbackImage?.currentSrc || fallbackImage?.src);
      if (img) { img.alt = title; img.hidden = !src; if (src) img.src = src; else img.removeAttribute('src'); }
      if (size) { size.textContent = optionText || 'Única'; size.hidden = false; }
      slot.setAttribute('aria-label', 'Camiseta ' + (index + 1) + ': ' + title + (optionText ? ', ' + optionText : ''));
    });
    tray.hidden = false;
    controller.root.querySelector('[data-wn-offer-dock]')?.classList.add('wn-offer-dock--ready');
    message(controller.root.querySelector('[data-wn-bundle-status]'), Math.min(total, 3) + ' de 3 camisetas elegidas');
    message(controller.root.querySelector('[data-wn-bundle-note]'), 'Camisetas seleccionadas · Comprueba el 3×2 en el carrito.');
    const extra = controller.root.querySelector('[data-wn-bundle-extra]');
    if (extra) { extra.textContent = total > 3 ? '+' + (total - 3) + ' camiseta' + (total - 3 === 1 ? '' : 's') + ' más' : ''; extra.hidden = total <= 3; }
    controller.root.removeAttribute('data-wn-cart-needs-check');
  }

  function applyCart(cart) {
    if (!validCart(cart)) return false;
    lastCart = cart;
    updateCount(cartCount(cart));
    controllers.forEach(controller => renderBundle(controller, cart));
    return true;
  }

  function celebrateConfirmedThird(controller, before, after, addedId, quantity) {
    if (!controller.root.isConnected) return;
    const dock = controller.root.querySelector('[data-wn-offer-dock]');
    if (!dock || !controller.root.querySelector('[data-wn-bundle]')
      || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const eligible = new Set();
    controller.cards.filter(card => card.element.dataset.wnOfferEligible === 'true')
      .forEach(card => card.variants.forEach(variant => eligible.add(variantId(variant.id))));
    if (!eligible.has(addedId)) return;
    const count = cart => cart.items.reduce((sum, item) => sum
      + (eligible.has(variantId(item.variant_id || item.id)) ? Number(item.quantity) : 0), 0);
    const previous = count(before), current = count(after);
    if (previous >= 3 || current < 3 || current - previous !== quantity
      || (quantities(after).get(addedId) || 0) - (quantities(before).get(addedId) || 0) !== quantity) return;
    clearTimeout(controller.celebrationTimer);
    dock.classList.remove('wn-bundle-celebrate');
    void dock.offsetWidth;
    dock.classList.add('wn-bundle-celebrate');
    controller.celebrationTimer = setTimeout(() => dock.classList.remove('wn-bundle-celebrate'), 700);
  }

  function bundleNeedsCheck(text = 'No podemos actualizar tu selección. Revísala en el carrito.') {
    controllers.forEach(controller => {
      controller.root.dataset.wnCartNeedsCheck = 'true';
      const tray = controller.root.querySelector('[data-wn-bundle]');
      const hasKnownSelection = Boolean(tray && !tray.hidden);
      controller.root.querySelector('[data-wn-offer-dock]')?.classList.toggle('wn-offer-dock--ready', hasKnownSelection);
      message(controller.root.querySelector('[data-wn-bundle-status]'), hasKnownSelection ? 'Última selección confirmada' : 'Tu selección está pendiente de comprobar');
      message(controller.root.querySelector('[data-wn-bundle-note]'), 'Camisetas seleccionadas. ' + text);
      // Keep the real cart reachable even when its latest count is unknown.
      all(controller.root, '.wn-cart-access').forEach(link => { link.hidden = false; });
    });
  }

  function nativeDrawer() {
    return window.theme && window.theme.settings && window.theme.settings.cartType === 'drawer'
      && document.getElementById('CartDrawer') && document.getElementById('CartDrawerForm');
  }

  function scheduleCountRefresh() {
    if (countRefreshScheduled || !getController()) return;
    countRefreshScheduled = true;
    // Reconcile initial/native cart state without opening anything or posting.
    // Never let an older read override a newer authoritative response.
    Promise.resolve().then(async () => {
      countRefreshScheduled = false;
      const controller = getController();
      if (!controller) return;
      const revision = ++countRevision;
      try {
        const cart = await controller.readCart();
        if (revision === countRevision) applyCart(cart);
      } catch (_) { if (revision === countRevision) bundleNeedsCheck(); }
    });
  }

  function bindNativeDrawerCounts() {
    const el = nativeDrawer() ? document.getElementById('CartDrawer') : null;
    if (el === nativeObservedElement) return;
    if (nativeObserver) nativeObserver.disconnect();
    nativeObserver = null; nativeObservedElement = el;
    if (!el || typeof MutationObserver !== 'function') return;
    let wasOpen = el.classList.contains('drawer--is-open');
    nativeObserver = new MutationObserver(() => {
      const isOpen = el.classList.contains('drawer--is-open');
      if (wasOpen && !isOpen) scheduleCountRefresh();
      wasOpen = isOpen;
    });
    nativeObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  function lockScroll() {
    if (scrollLocked) return;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      const style = document.body.style;
      fixedBodySnapshot = { x: window.scrollX, y: window.scrollY, position: style.position,
        top: style.top, left: style.left, right: style.right, width: style.width };
      style.position = 'fixed'; style.top = '-' + fixedBodySnapshot.y + 'px';
      style.left = '0'; style.right = '0'; style.width = '100%';
    }
    scrollLocked = true;
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    document.body.style.overflow = previousOverflow;
    if (fixedBodySnapshot) {
      const snapshot = fixedBodySnapshot;
      ['position', 'top', 'left', 'right', 'width'].forEach(property => { document.body.style[property] = snapshot[property]; });
      const previousBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(snapshot.x, snapshot.y);
      document.documentElement.style.scrollBehavior = previousBehavior;
      fixedBodySnapshot = null;
    }
    scrollLocked = false;
  }

  function closeCart() {
    const el = dialog();
    if (!el) return;
    if (typeof el.close === 'function' && el.open) el.close();
    else { el.removeAttribute('open'); restoreFocus(); }
  }
  function restoreFocus() {
    unlockScroll();
    if (cartOpener && cartOpener.isConnected) {
      const target = cartOpener.getClientRects().length ? cartOpener
        : getController()?.root.querySelector('.wn-nav a[href="#bestsellers"]');
      if (target) target.focus({ preventScroll: true });
    }
    cartOpener = null;
  }
  function bindDialog() {
    const el = dialog();
    if (!el || boundDialogs.has(el)) return;
    boundDialogs.add(el);
    el.addEventListener('close', restoreFocus);
    el.addEventListener('click', event => {
      if (event.target !== el) return;
      const box = el.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeCart();
    });
    el.addEventListener('keydown', event => {
      if (event.key === 'Escape' && typeof el.showModal !== 'function') { event.preventDefault(); closeCart(); }
      if (event.key !== 'Tab') return;
      const focusable = all(el, 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]')
        .filter(node => !node.hidden && node.getClientRects().length);
      if (!focusable.length) { event.preventDefault(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === el)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }
  function openOwnCart(opener) {
    bindDialog();
    const el = dialog();
    if (!el) return false;
    if (!el.open) {
      cartOpener = opener && opener.isConnected ? opener : document.activeElement;
      lockScroll();
      try {
        if (typeof el.showModal === 'function') el.showModal();
        else { el.setAttribute('open', ''); el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); }
      } catch (_) { restoreFocus(); return false; }
      const focus = el.querySelector('[data-wn-cart-close]') || el;
      if (!focus.hasAttribute('tabindex') && focus === el) focus.tabIndex = -1;
      focus.focus({ preventScroll: true });
    }
    return true;
  }

  function renderSection(html) {
    if (typeof html !== 'string' || !html.trim()) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingSection = parsed.querySelector('[data-wn-cart-section]');
    const incoming = incomingSection && incomingSection.querySelector('[data-wn-cart-content]');
    const liveSection = cartSection();
    const current = liveSection && liveSection.querySelector('[data-wn-cart-content]');
    if (!incoming || !current) return false;
    const focused = document.activeElement;
    const restoreKey = focused && focused.closest('[data-wn-change]') && focused.dataset.key;
    current.replaceChildren(...Array.from(incoming.childNodes).map(node => document.importNode(node, true)));
    operation.staleView = false;
    if (incoming.hasAttribute('data-wn-cart-count')) current.dataset.wnCartCount = incoming.dataset.wnCartCount;
    const countNode = current.matches('[data-wn-cart-count]') ? current : current.querySelector('[data-wn-cart-count]');
    if (countNode) updateCount(Number(countNode.dataset.wnCartCount));
    if (restoreKey) {
      const replacement = all(current, '[data-wn-change]').find(node => node.dataset.key === restoreKey)
        || (dialog() && dialog().querySelector('[data-wn-cart-close]'));
      if (replacement) replacement.focus({ preventScroll: true });
    }
    syncBusy();
    return true;
  }

  async function refreshSection(data) {
    const revision = operation.revision;
    const id = sectionId();
    if (!id) return false;
    if (data && data.sections && renderSection(data.sections[id])) return true;
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', id);
      const sections = await request(url.pathname + url.search, { method: 'GET', headers: { Accept: 'application/json' } });
      // A cart read started before a later mutation must never paint stale lines.
      if (revision !== operation.revision) return null;
      return renderSection(sections[id]);
    } catch (_) { return false; }
  }

  function syncBusy() {
    const locked = operation.busy || Boolean(operation.uncertain) || nativeOpenPending;
    controllers.forEach(controller => controller.cards.forEach(card => controller.renderCard(card)));
    const section = cartSection();
    if (section) {
      section.setAttribute('aria-busy', String(operation.busy));
      all(section, '[data-wn-change]').forEach(button => { button.disabled = locked || operation.staleView; });
      all(section, '[data-wn-reconcile]').forEach(button => { button.disabled = operation.busy; });
    }
  }

  function quantities(cart) {
    const map = new Map();
    (cart.items || []).forEach(item => {
      const key = variantId(item.variant_id || item.id);
      map.set(key, (map.get(key) || 0) + Number(item.quantity || 0));
    });
    return map;
  }
  function matchesExpected(cart, attempt) {
    if (attempt.kind === 'change') {
      const line = (cart.items || []).find(item => item.key === attempt.key);
      return Number(line ? line.quantity : 0) === attempt.quantity;
    }
    const before = quantities(attempt.before), now = quantities(cart);
    before.set(attempt.variant, (before.get(attempt.variant) || 0) + attempt.quantity);
    return before.size === now.size && Array.from(before).every(([id, quantity]) => now.get(id) === quantity);
  }
  function showUncertain(attempt) {
    operation.uncertain = attempt;
    const text = attempt.rejected422
      ? attempt.rejectionMessage + ' Debemos comprobar el carrito antes de continuar: la disponibilidad puede haber ajustado la cantidad.'
      : 'La conexión se ha cortado y no podemos confirmar el cambio. No vuelvas a añadirlo: comprueba primero el carrito.';
    message(attempt.status, text);
    cartMessage(text);
    bundleNeedsCheck('Cambio pendiente de confirmar. Comprueba el carrito antes de seguir.');
    const hosts = [attempt.status, cartSection() && cartSection().querySelector('[data-wn-cart-message]')].filter(Boolean);
    hosts.forEach(host => {
      if (host.querySelector('[data-wn-reconcile]')) return;
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.wnReconcile = '';
      button.className = 'wn-reconcile'; button.textContent = 'Comprobar carrito';
      button.addEventListener('click', event => { event.preventDefault(); event.stopImmediatePropagation(); reconcile(); });
      host.append(document.createTextNode(' '), button);
    });
  }
  function rejectedMessage(attempt, cart) {
    if (attempt.kind === 'add') {
      const quantity = quantities(cart).get(attempt.variant) || 0;
      return attempt.rejectionMessage + ' El carrito contiene ahora ' + quantity + ' unidad' + (quantity === 1 ? '' : 'es') + ' de esta talla. Revisa la cantidad antes de volver a añadir.';
    }
    return attempt.rejectionMessage + ' Hemos consultado el carrito. Revisa las cantidades actualizadas.';
  }
  async function reconcileRejected(attempt, error) {
    // Shopify may add the maximum in-stock quantity AND return 422. The error
    // response alone is therefore never evidence that no mutation occurred.
    attempt.rejected422 = true; attempt.rejectionMessage = error.message;
    operation.uncertain = attempt;
    message(attempt.status, error.message + ' Comprobando la cantidad del carrito…');
    try {
      const cart = await attempt.controller.readCart();
      applyCart(cart);
      operation.uncertain = null;
      const text = rejectedMessage(attempt, cart);
      message(attempt.status, text); cartMessage(text);
      // A real read may show a partial addition. Do not emit ajaxProduct:added
      // on this path, and never retry the original POST.
    } catch (_) { showUncertain(attempt); }
  }
  async function reconcile() {
    const attempt = operation.uncertain;
    if (!attempt || operation.busy) return;
    operation.busy = true; syncBusy();
    try {
      const cart = await attempt.controller.readCart();
      applyCart(cart);
      if (attempt.rejected422 || matchesExpected(cart, attempt)) {
        operation.uncertain = null;
        const text = attempt.rejected422 ? rejectedMessage(attempt, cart) : 'Cambio confirmado. Tu selección está actualizada.';
        message(attempt.status, text);
        cartMessage(text);
      } else {
        showUncertain(attempt);
        cartMessage('Revisa las cantidades en tu carrito antes de continuar. El cambio aún no está confirmado; las nuevas acciones siguen bloqueadas. Recarga la página después de comprobarlo.');
      }
    } catch (_) { showUncertain(attempt); }
    finally { operation.busy = false; syncBusy(); }
  }

  class Landing {
    constructor(root) {
      this.root = root; this.cards = [];
      const config = JSON.parse(root.querySelector('script[data-wn-config]').textContent);
      this.routes = {
        add: endpoint(config.routes.add, 'add'), change: endpoint(config.routes.change, 'change'),
        cart: endpoint(config.routes.cart, 'cart'), cartPage: new URL(config.routes.cart, location.origin).pathname.replace(/\.js$/, '')
      };
      this.config = config;
      all(root, '[data-wn-card]').forEach(element => {
        const form = element.querySelector('form[data-wn-form]');
        const master = element.querySelector('[data-wn-master]');
        const source = element.querySelector('script[data-wn-variants]');
        if (!form || !master || !source) return;
        try {
          const variants = JSON.parse(source.textContent);
          if (!Array.isArray(variants) || !variants.length) return;
          if (variants.some(variant => !variant || variant.id == null || typeof variant.available !== 'boolean'
            || !Array.isArray(variant.options) || variant.options.some(value => typeof value !== 'string'))) return;
          const card = { element, form, master, variants, selected: Array((variants[0].options || []).length).fill(null),
            variant: null, add: element.querySelector('[data-wn-add]'), status: element.querySelector('[data-wn-status]'),
            basePrice: element.querySelector('[data-wn-price]')?.textContent || '', baseCompare: element.querySelector('[data-wn-compare]')?.textContent || '' };
          if (!card.add) return;
          if (variants.length === 1) card.selected = (variants[0].options || []).slice();
          card.master.required = false; card.master.hidden = true; card.master.tabIndex = -1;
          card.master.setAttribute('aria-hidden', 'true');
          all(element, '[data-wn-master-label]').forEach(label => { label.hidden = true; });
          const defaultOnly = variants.length === 1 && variants[0].options.every(value => value === 'Default Title');
          all(element, '[data-wn-options]').forEach(fieldset => { fieldset.hidden = defaultOnly; });
          const quickOpen = element.querySelector('[data-wn-quick-open]');
          const quickSummary = element.querySelector('.wn-quickbuy > summary');
          if (quickOpen && quickSummary) quickOpen.setAttribute('aria-label', quickSummary.getAttribute('aria-label') || 'Elegir talla');
          this.cards.push(card);
          this.renderCard(card);
        } catch (_) { /* Invalid card JSON keeps its native, no-JS product form usable. */ }
      });
      root.classList.add('wn-is-enhanced', 'disable-monster');
      this.onClick = this.click.bind(this); this.onSubmit = this.submit.bind(this);
      root.addEventListener('click', this.onClick, true);
      root.addEventListener('submit', this.onSubmit, true);
      bindDialog();
      this.dock = root.querySelector('[data-wn-offer-dock]');
      this.measureDock = () => {
        if (!root.isConnected) return;
        const height = Math.ceil(this.dock?.getBoundingClientRect().height || 0);
        root.style.setProperty('--wn-dock-height', height + 'px');
        const maxHeight = Math.max(height, ...Array.from(controllers).map(controller => Math.ceil(controller.dock?.getBoundingClientRect().height || 0)));
        document.body.style.setProperty('--wn-dock-height', maxHeight + 'px');
      };
      if (this.dock && typeof ResizeObserver === 'function') {
        this.dockObserver = new ResizeObserver(this.measureDock);
        this.dockObserver.observe(this.dock);
      } else window.addEventListener('resize', this.measureDock);
      this.measureDock();
      this.scrollToIntent();
    }

    destroy() {
      this.root.removeEventListener('click', this.onClick, true);
      this.root.removeEventListener('submit', this.onSubmit, true);
      this.dockObserver?.disconnect();
      window.removeEventListener('resize', this.measureDock);
      this.root.style.removeProperty('--wn-dock-height');
      clearTimeout(this.celebrationTimer);
      this.dock?.classList.remove('wn-bundle-celebrate');
      if (cartOpenAttempt?.controller === this) {
        cartOpenAttempt.cancelled = true;
        cartOpenAttempt.opener.removeAttribute('aria-busy');
        cartOpenAttempt = null; nativeOpenPending = false;
      }
      controllers.delete(this); boundRoots.delete(this.root);
      if (!controllers.size) document.body.style.removeProperty('--wn-dock-height');
      else controllers.forEach(controller => controller.measureDock());
      if (!controllers.size) {
        monsterObserver?.disconnect(); monsterObserver = null;
        monsterObservedElement = null; monsterOpener = null;
      }
      if (dialog() && !dialog().isConnected) restoreFocus();
    }

    scrollToIntent() {
      const requested = new URLSearchParams(location.search).get('section');
      if (!requested) return;
      const target = all(this.root, '[data-wn-intent][id]').find(node => node.dataset.wnIntent === requested);
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
    }

    renderCard(card) {
      const locked = operation.busy || Boolean(operation.uncertain) || nativeOpenPending;
      const complete = card.selected.every(value => value !== null);
      card.variant = complete ? card.variants.find(variant => variant.options.every((value, index) => value === card.selected[index])) || null : null;
      const sellable = card.variant && card.variant.available;
      card.master.value = card.variant ? variantId(card.variant.id) : '';
      card.add.disabled = locked || !sellable;
      card.add.setAttribute('aria-disabled', String(card.add.disabled));
      const addingThisCard = operation.busy && operation.activeCard === card;
      const availableAny = card.variants.some(variant => variant.available);
      const label = addingThisCard ? 'Añadiendo…' : !availableAny ? 'Agotada'
        : !complete ? (card.selected.length > 1 ? 'Elige las opciones' : 'Elige tu talla')
        : sellable ? 'Añadir al carrito' : 'Agotada';
      message(card.add.querySelector('[data-wn-add-label]') || card.add, label);
      card.form.setAttribute('aria-busy', String(addingThisCard));
      all(card.element, '[data-wn-option]').forEach(button => {
        const index = Number(button.dataset.index), value = button.dataset.value;
        const possible = card.variants.some(variant => variant.available && variant.options[index] === value
          && card.selected.slice(0, index).every((selected, i) => selected === null || variant.options[i] === selected));
        button.disabled = locked || !possible;
        button.setAttribute('aria-disabled', String(button.disabled));
        button.setAttribute('aria-pressed', String(card.selected[index] === value));
      });
      message(card.element.querySelector('[data-wn-price]'), card.variant ? card.variant.price : card.basePrice);
      const compare = card.element.querySelector('[data-wn-compare]');
      const compareText = card.variant ? card.variant.compare : card.baseCompare;
      if (compare) { compare.textContent = compareText || ''; compare.hidden = !compareText; }
    }

    click(event) {
      const offerLink = event.target.closest('a[href="#wn-offer-conditions"]');
      if (offerLink && this.root.contains(offerLink) && event.button === 0
        && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const conditions = this.root.querySelector('#wn-offer-conditions');
        if (conditions) conditions.open = true;
        return; // Keep native anchor navigation and existing URL parameters.
      }
      const quickOpen = event.target.closest('[data-wn-quick-open]');
      if (quickOpen && this.root.contains(quickOpen)) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const card = this.cards.find(item => item.element.contains(quickOpen));
        const disclosure = card && card.element.querySelector('.wn-quickbuy');
        if (!disclosure) return;
        event.preventDefault(); event.stopImmediatePropagation();
        disclosure.open = true;
        disclosure.querySelector('summary')?.focus({ preventScroll: true });
        disclosure.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        return;
      }
      const button = event.target.closest('[data-wn-option], [data-wn-add]');
      if (!button || !this.root.contains(button)) return;
      const card = this.cards.find(item => item.element.contains(button));
      if (!card) return;
      // Document capture app handlers skip .disable-monster. Stop the subsequent
      // target/bubble listeners so this CTA has one owner, not a second app POST.
      event.preventDefault(); event.stopImmediatePropagation();
      if (button.disabled) return;
      if (button.matches('[data-wn-add]')) {
        if (typeof card.form.requestSubmit === 'function') card.form.requestSubmit(button);
        else this.add(card);
        return;
      }
      const index = Number(button.dataset.index);
      card.selected[index] = button.dataset.value;
      for (let next = index + 1; next < card.selected.length; next++) {
        if (!card.variants.some(variant => variant.available && card.selected.slice(0, next + 1)
          .every((value, i) => value === null || variant.options[i] === value))) card.selected[next] = null;
      }
      this.renderCard(card);
      const selected = card.variant;
      message(card.status, selected ? (selected.available ? 'Talla seleccionada. Puedes añadirla al carrito.' : 'Esta combinación está agotada.') : 'Elige las opciones para añadirla al carrito.');
      if (selected && typeof selected.image === 'string') {
        const img = card.element.querySelector('[data-wn-product-image], [data-wn-image]');
        if (img) {
          try { const url = new URL(selected.image, location.origin); if (url.protocol === 'https:' || url.origin === location.origin) { img.src = url.href; img.removeAttribute('srcset'); } } catch (_) { /* Preserve existing product image. */ }
        }
      }
    }

    submit(event) {
      const card = this.cards.find(item => item.form === event.target);
      if (!card) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.add(card);
    }

    async readCart() {
      const cart = await request(this.routes.cart, { method: 'GET', headers: { Accept: 'application/json' } });
      if (!validCart(cart)) throw new Error('No se ha podido leer el carrito.');
      return cart;
    }

    async add(card) {
      if (operation.busy || operation.uncertain || nativeOpenPending) return;
      if (!card.variant || !card.variant.available) { message(card.status, 'Elige una talla disponible.'); return; }
      const selected = card.variant;
      operation.busy = true; operation.controller = this; operation.activeCard = card; operation.revision++; countRevision++; syncBusy();
      message(card.status, 'Añadiendo al carrito…'); cartMessage('');
      let attempted = false, attempt;
      try {
        const before = await this.readCart();
        const form = new FormData(card.form);
        const requested = Number(form.get('quantity') || 1);
        const quantity = Number.isInteger(requested) && requested > 0 ? requested : 1;
        form.set('id', variantId(selected.id)); form.set('quantity', String(quantity));
        attempt = { kind: 'add', before, variant: variantId(selected.id), quantity, controller: this, status: card.status, opener: card.add };
        attempted = true;
        const result = await request(this.routes.add, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: form }, true);
        const returnedItems = result && Array.isArray(result.items) ? result.items : [result];
        if (!returnedItems.some(item => item && variantId(item.variant_id || item.id) === variantId(selected.id))) {
          const error = new Error('No se ha podido confirmar el producto añadido.');
          error.uncertain = true; throw error;
        }
        message(card.status, 'Añadida. Puedes seguir eligiendo.');
        try {
          const after = await this.readCart();
          if (applyCart(after)) celebrateConfirmedThird(this, before, after, variantId(selected.id), quantity);
        }
        catch (_) {
          // The POST is confirmed: do not turn a failed read into a retry.
          bundleNeedsCheck('La camiseta se ha añadido. Revisa tu selección en el carrito.');
          message(card.status, 'Añadida al carrito. No hemos podido actualizar la selección; revísala en el carrito.');
        }
      } catch (error) {
        if (attempted && error.status === 422) await reconcileRejected(attempt, error);
        else if (attempted && error.uncertain) showUncertain(attempt);
        else message(card.status, attempted ? error.message : 'No hemos podido conectar con el carrito. No se ha enviado ningún producto. Inténtalo de nuevo.');
      } finally { operation.busy = false; operation.activeCard = null; syncBusy(); }
    }

    async change(button) {
      if (operation.busy || operation.uncertain || operation.staleView) return;
      const key = button.dataset.key, quantity = Number(button.dataset.quantity);
      if (!key || !Number.isInteger(quantity) || quantity < 0) return;
      operation.busy = true; operation.controller = this; operation.activeCard = null; operation.revision++; countRevision++; syncBusy();
      cartMessage('Actualizando carrito…');
      const attempt = { kind: 'change', key, quantity, controller: this, status: cartSection()?.querySelector('[data-wn-cart-message]'), opener: button };
      try {
        const id = sectionId();
        const data = { id: key, quantity, ...(id ? { sections: [id], sections_url: location.pathname } : {}) };
        const cart = await request(this.routes.change, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(data) }, true);
        if (!cart || !Array.isArray(cart.items) || cartCount(cart) === null) {
          const error = new Error('No se ha podido confirmar el cambio del carrito.');
          error.uncertain = true; throw error;
        }
        applyCart(cart);
        const rendered = await refreshSection(cart);
        operation.staleView = !rendered;
        cartMessage(rendered ? 'Carrito actualizado.' : 'El cambio se ha guardado. No hemos podido actualizar la vista; abre el carrito completo para revisarlo.');
      } catch (error) {
        if (error.status === 422) await reconcileRejected(attempt, error);
        else if (error.uncertain) showUncertain(attempt);
        else cartMessage(error.message);
      } finally { operation.busy = false; syncBusy(); }
    }
  }

  function boot(scope = document) {
    const roots = scope.matches && scope.matches('[data-wn-landing]') ? [scope] : all(scope, '[data-wn-landing]');
    roots.forEach(root => {
      if (boundRoots.has(root)) return;
      try {
        const controller = new Landing(root);
        controllers.add(controller); boundRoots.set(root, controller);
        if (lastCart) renderBundle(controller, lastCart);
      } catch (_) { /* Missing config: native Liquid forms remain operational. */ }
    });
    bindDialog(); bindNativeDrawerCounts(); syncBusy(); scheduleCountRefresh();
  }

  function cartFeedback(controller, text, retry = false) {
    const host = controller.root.querySelector('[data-wn-cart-feedback]');
    if (!host) return;
    host.hidden = !text;
    message(host.querySelector('[data-wn-cart-status]'), text);
    const button = host.querySelector('[data-wn-cart-retry]');
    if (button) { button.hidden = !retry; button.disabled = nativeOpenPending; }
  }

  function monsterCartReady() {
    const wrapper = document.getElementById('monster-cart-wrapper');
    return wrapper && !window.upsellCartStopped
      && ['openCart', 'monster_refresh', 'monster_setCartItems', 'mu_productionAddToCartCallBack']
        .every(name => typeof window[name] === 'function') ? wrapper : null;
  }

  function waitForCartState(check, timeoutMs, active, failure) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const poll = () => {
        if (!active()) { reject(new Error('Solicitud de carrito cancelada.')); return; }
        let result;
        try { result = check(); } catch (error) { reject(error); return; }
        if (result) { resolve(result); return; }
        if (Date.now() - started >= timeoutMs) { reject(new Error(failure)); return; }
        setTimeout(poll, 80);
      };
      poll();
    });
  }

  function refreshMonsterCart(active) {
    // Observed Monster 1021/1023 API: the promise resolves even after a caught
    // error. Only its explicit success callback confirms state/campaign refresh.
    // false prevents any late completion from opening an abandoned request.
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = error => {
        if (settled) return;
        settled = true; clearTimeout(timeout);
        if (error) reject(error); else resolve();
      };
      const timeout = setTimeout(() => finish(new Error('El carrito no ha terminado de actualizarse.')), 12000);
      try {
        const pending = window.monster_refresh(false, () => {
          if (!active()) { finish(new Error('Solicitud de carrito cancelada.')); return; }
          finish();
        });
        Promise.resolve(pending).catch(error => finish(error));
      } catch (error) { finish(error); }
    });
  }

  function afterRenderFrames() {
    return new Promise(resolve => {
      let done = false;
      const finish = () => { if (!done) { done = true; clearTimeout(timeout); resolve(); } };
      const timeout = setTimeout(finish, 300);
      requestAnimationFrame(() => requestAnimationFrame(finish));
    });
  }

  function observeMonsterClose(wrapper, opener) {
    monsterOpener = opener;
    if (wrapper === monsterObservedElement) return;
    monsterObserver?.disconnect(); monsterObservedElement = wrapper;
    if (typeof MutationObserver !== 'function') return;
    let wasOpen = wrapper.classList.contains('mu_openned');
    monsterObserver = new MutationObserver(() => {
      const isOpen = wrapper.classList.contains('mu_openned');
      if (wasOpen && !isOpen) {
        scheduleCountRefresh();
        const target = monsterOpener?.isConnected && monsterOpener.getClientRects().length ? monsterOpener
          : getController()?.root.querySelector('.wn-nav a[href="#bestsellers"]');
        if (target) target.focus({ preventScroll: true });
        monsterOpener = null;
      }
      wasOpen = isOpen;
    });
    monsterObserver.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }

  async function openRealCart(controller, opener) {
    if (nativeOpenPending) return;
    const attempt = { controller, opener, cancelled: false };
    cartOpenAttempt = attempt; nativeOpenPending = true;
    const active = () => cartOpenAttempt === attempt && !attempt.cancelled && controller.root.isConnected;
    opener.setAttribute('aria-busy', 'true'); syncBusy();
    cartFeedback(controller, 'Preparando tu carrito…');
    try {
      await waitForCartState(monsterCartReady, 8000, active, 'El carrito aún no está disponible.');
      const cart = await controller.readCart();
      if (!active()) return;
      applyCart(cart);
      cartFeedback(controller, 'Actualizando tu carrito y sus promociones…');
      await refreshMonsterCart(active);
      await afterRenderFrames();
      if (!active()) return;
      if (!monsterCartReady() || document.visibilityState === 'hidden') throw new Error('No hemos podido abrir el carrito.');
      // Exactly one explicit open. No ATC event, Impulse event, toggle or redirect.
      window.openCart();
      const wrapper = await waitForCartState(() => {
        const element = document.getElementById('monster-cart-wrapper');
        if (!element?.classList.contains('mu_openned')) return null;
        const box = element.getBoundingClientRect(), style = getComputedStyle(element);
        return box.width > 0 && box.height > 0 && box.left < window.innerWidth && box.right > 0
          && style.display !== 'none' && style.visibility !== 'hidden' ? element : null;
      }, 2500, active, 'El carrito no se ha abierto.');
      observeMonsterClose(wrapper, opener);
      cartFeedback(controller, '');
      scheduleCountRefresh();
    } catch (_) {
      if (active()) cartFeedback(controller, 'No hemos podido abrir el carrito con sus promociones. Pulsa «Reintentar carrito» sin volver a añadir productos.', true);
    } finally {
      if (cartOpenAttempt === attempt) {
        cartOpenAttempt = null; nativeOpenPending = false;
        opener.removeAttribute('aria-busy'); syncBusy();
        const retry = controller.root.querySelector('[data-wn-cart-retry]');
        if (retry) retry.disabled = false;
      }
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wn-cart-open], [data-wn-cart-retry], [data-wn-cart-close], [data-wn-change]');
    if (!button || !(button.closest('[data-wn-landing]') || button.closest('[data-wn-cart-section]'))) return;
    const controller = getController();
    if (!controller) return;
    if (button.matches('[data-wn-cart-open]') && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.matches('[data-wn-cart-close]')) { closeCart(); return; }
    if (button.matches('[data-wn-change]')) { if (!button.disabled) controller.change(button); return; }
    if (operation.busy) { bundleNeedsCheck('Estamos guardando tu selección. Espera un momento antes de abrir el carrito.'); return; }
    openRealCart(controller, button);
  }, true);

  document.addEventListener('drawerOpen.CartDrawer', () => {
    if (nativeDrawer()) scheduleCountRefresh();
  });
  document.addEventListener('cart:updated', event => {
    if (!getController()) return;
    const cart = event.detail && event.detail.cart;
    if (validCart(cart)) applyCart(cart);
    else scheduleCountRefresh();
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') scheduleCountRefresh(); });
  window.addEventListener('pageshow', event => { if (event.persisted) scheduleCountRefresh(); });

  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach(controller => { if (event.target.contains(controller.root)) controller.destroy(); });
    if (event.target.querySelector('[data-wn-cart-dialog]')) restoreFocus();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();
